import React from 'react'
import {EmptyKegsForm, EmptyKegsFormKeysArray} from '../../types'
import {FetchVenuesResponse as Address} from '../../services'
import {kegQuantityInputRegex} from '../../utils'
import FormErrorMessage from '../FormErrorMessage/FormErrorMessage'

function handleOnBeforeInput(e: React.FormEvent<HTMLInputElement>) {
  const ev = e.nativeEvent as InputEvent & {isComposing?: boolean}

  // If the input event is a composition event (e.g., for IME input), skip validation
  if (ev.isComposing) return

  const {value, selectionStart, selectionEnd} = e.currentTarget as HTMLInputElement
  const selStart = selectionStart ?? value.length
  const selEnd = selectionEnd ?? selStart

  const insertText = ev.data ?? ''
  const nextValue = value.slice(0, selStart) + insertText + value.slice(selEnd)

  const isDelete = ev.inputType?.startsWith('delete')

  const matchesRegex = kegQuantityInputRegex.test(nextValue)
  if (!matchesRegex && !(isDelete && nextValue === '')) e.preventDefault()
}

function handleOnPaste(e: React.ClipboardEvent<HTMLInputElement>) {
  // Allow pasting only valid input, safeguard to prevent
  // bypassing validation logic in handleOnBeforeInput function
  const text = e.clipboardData.getData('text')
  const {value, selectionStart, selectionEnd} = e.currentTarget as HTMLInputElement
  const selStart = selectionStart ?? value.length
  const selEnd = selectionEnd ?? selStart

  const nextValue = value.slice(0, selStart) + text + value.slice(selEnd)
  if (!kegQuantityInputRegex.test(nextValue)) e.preventDefault()
}

interface KegQuantityInputsProps {
  formValues: EmptyKegsForm
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isDisabled: boolean
  inputRef: React.RefObject<HTMLInputElement> | null
  flagFormErrors?: boolean
  chosenLocation?: Address | null
}

const KegQuantityInputs = ({
  formValues,
  onChange,
  isDisabled,
  inputRef,
  flagFormErrors,
  chosenLocation,
}: KegQuantityInputsProps) => {
  const noQuantityEntered = EmptyKegsFormKeysArray.every(
    (size) => formValues[size as keyof EmptyKegsForm] === ''
  )

  const kegSizes =
    chosenLocation?.country === 'AU' || chosenLocation?.country === 'NZ'
      ? EmptyKegsFormKeysArray.filter((size: string) => size !== '9G')
      : EmptyKegsFormKeysArray

  return (
    <div className="mb-5">
      <div className="flex flex-col items-center gap-3">
        {kegSizes.map((size, index) => (
          <div
            className="flex items-center justify-center"
            key={size}
          >
            <label
              className={`${flagFormErrors && noQuantityEntered ? 'text-red-600 dark:text-red-300' : 'text-slate-700 dark:text-slate-200'} font-bold w-[40px]`}
              htmlFor={size}
            >
              {size}
            </label>
            <input
              className={[
                'max-w-24 border rounded py-2 px-3',
                'text-gray-700 dark:text-slate-200 leading-none focus:outline-none dark:bg-slate-900/60',
                `${flagFormErrors && noQuantityEntered ? 'focus:border-red-500 dark:focus:border-red-400/90' : 'focus:border-sky-500 dark:focus:border-sky-400'}`,
                `${flagFormErrors && noQuantityEntered ? 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-950/30' : 'border-gray-300 dark:border-slate-600'}`,
              ].join(' ')}
              id={size}
              name={size}
              type="number"
              inputMode="numeric" // codeCommentsOpenMobileVirtualKeyboardOnAutoFocus
              tabIndex={0} // codeCommentsOpenMobileVirtualKeyboardOnAutoFocus
              value={formValues[size as keyof EmptyKegsForm]}
              onChange={onChange}
              disabled={isDisabled}
              ref={inputRef && index === 0 ? inputRef : null}
              onBeforeInput={handleOnBeforeInput}
              onPaste={handleOnPaste}
              min={1}
              step={1}
            />
          </div>
        ))}
      </div>
      {flagFormErrors && noQuantityEntered && (
        <FormErrorMessage message="Must select at least one keg size with a quantity greater than zero." />
      )}
    </div>
  )
}

export default React.memo(KegQuantityInputs)
